import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useBranding, resolveImageUrl } from '@/contexts/BrandingContext';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import ThemeToggle from '@/components/ThemeToggle';
import {
  Building2,
  Send,
  Loader2,
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  Home,
  Instagram,
  Clock,
  User,
  HelpCircle,
  CheckCircle2,
} from 'lucide-react';

const PROPERTY_TYPES = ['Hotel', 'Cabaña', 'Departamento', 'Casa', 'Hostel', 'Otro'];

const OWNER_FAQS = [
  { q: '¿Qué es Estadía 365?', a: 'Estadía 365 es una empresa especializada en alquileres temporarios, vacacionales y turísticos. Ayudamos a hoteles, cabañas, departamentos, casas privadas y alojamientos turísticos a captar más reservas a través de Airbnb, redes sociales y gestión profesional.' },
  { q: '¿Qué servicio ofrecen?', a: 'Sumamos tu alojamiento como un canal adicional de reservas. Nos encargamos de publicar, promocionar y gestiónar consultas para que tu propiedad pueda llegar a huéspedes nacionales e internacionales.' },
  { q: '¿Solo publican alojamientos en Airbnb?', a: 'No. Nuestro trabajo no es únicamente cargar un anuncio. También ayudamos a mejorar la presentación del alojamiento, orientar sobre tarifas, responder consultas, coordinar disponibilidad y acompañar el proceso de reserva.' },
  { q: '¿Por qué conviene trabajar con Estadía 365?', a: 'Porque contamos con más de 10 años de experiencia en el mercado, perfil con reputación de Superanfitrión y conocimiento real del funcionamiento de Airbnb. Eso permite que los alojamientos tengan mayor visibilidad, mejor gestión y más oportunidades de reserva.' },
  { q: '¿Qué tipo de alojamientos pueden sumarse?', a: 'Trabajamos con hoteles, apart hoteles, cabañas, departamentos, casas privadas, posadas, lodges, bungalows, resorts, complejos turísticos y también inmuebles que puedan adaptarse para alquiler temporario.' },
  { q: '¿Cómo es el proceso para sumar mi alojamiento?', a: 'Primero nos compartís la información principal del alojamiento: capacidad, ubicación, amenities, fotos, servicios, reglas, disponibilidad y tarifas. Luego acordamos las condiciones comerciales y preparamos la publicación o promoción del alojamiento.' },
  { q: '¿Quién define las tarifas?', a: 'Las tarifas se acuerdan previamente con el propietario, encargado u hotel. La idea es trabajar con valores claros desde el inicio para evitar confusiones y mantener una gestión ordenada.' },
  { q: '¿Qué pasa cuando un huésped consulta por una fecha?', a: 'Cuando recibimos una consulta, primero verificamos la disponibilidad con el alojamiento o propietario. Después de confirmar, se acepta o rechaza la reserva según corresponda.' },
  { q: '¿Cuándo se paga al alojamiento?', a: 'Una vez aceptada la reserva, Estadía 365 realiza el pago correspondiente al alojamiento antes de la llegada del huésped, de acuerdo con las condiciones previamente pactadas.' },
  { q: '¿Qué pasa si el huésped cancela?', a: 'Las condiciones de cancelación se acuerdan desde el inicio. De esa manera, ambas partes saben cómo actuar si el huésped cancela una reserva, especialmente si el pago ya fue realizado.' },
  { q: '¿Se puede hacer un contrato?', a: 'Sí. Podemos realizar un contrato o acuerdo con todos los detalles del trabajo para dar mayor seguridad tanto al propietario como a Estadía 365. Allí se pueden definir tarifas, disponibilidad, responsabilidades, pagos, cancelaciónes y condiciones generales.' },
  { q: '¿Pierdo el control de mi alojamiento?', a: 'No. El alojamiento sigue siendo tuyo. Estadía 365 funciona como un canal adicional de captación y gestión de reservas. Las condiciones, tarifas y disponibilidad se coordinan previamente con vos.' },
  { q: '¿Puedo seguir usando mis otros canales de venta?', a: 'Sí. Estadía 365 no reemplaza tus canales actuales. La idea es sumar Airbnb y redes sociales como una fuente adicional de reservas para ayudarte a mejorar la ocupación.' },
  { q: '¿También trabajan con hoteles?', a: 'Sí. Estadía 365 puede ayudar a hoteles, apart hoteles y complejos turísticos a sumar Airbnb como canal adicional de ventas, especialmente para habitaciones disponibles, temporadas bajas o capacidad ociosa.' },
  { q: '¿Qué es el servicio de home staging?', a: 'Es un asesoramiento para adaptar o mejorar un inmueble y hacerlo más atractivo para alquiler temporario. Puede incluir desde pequeñas modificaciones hasta renovaciones completas, según el estado y el potencial de la propiedad.' },
  { q: '¿Necesito tener fotos profesionales?', a: 'No es obligatorio, pero las fotos son muy importantes para generar confianza y atraer huéspedes. Si el alojamiento no tiene buenas imágenes, podemos orientarte sobre qué fotos se necesitan y cómo mejorar la presentación.' },
  { q: '¿Mi inmueble tiene que estar listo para alquilar?', a: 'No necesariamente. Si el inmueble todavía no está preparado, podemos asesorarte para adaptarlo al alquiler temporario y hacerlo más competitivo.' },
  { q: '¿Con qué tipo de huéspedes trabajan?', a: 'Trabajamos con huéspedes de todo el mundo: viajeros nacionales, internacionales, familias, grupos, turistas, personas que viajan por eventos, trabajo o descanso.' },
  { q: '¿Qué diferencia a Estadía 365 de publicar por mi cuenta?', a: 'La diferencia está en la experiencia, la reputación, la gestión y el conocimiento del mercado. Muchas personas pueden publicar en Airbnb, pero no todas saben posicionar bien un alojamiento, responder consultas, manejar disponibilidad, definir tarifas y generar confianza en los huéspedes.' },
  { q: '¿Cómo puedo sumar mi alojamiento?', a: 'Podés contactarnos y enviarnos la información básica de tu propiedad: ubicación, tipo de alojamiento, capacidad, fotos, amenities, tarifas estimadas y disponibilidad. Con esos datos evaluamos la mejor forma de incorporarlo a nuestra red.' },
];

export default function OwnerPage() {
  const { brandData } = useBranding();
  const [openFaq, setOpenFaq] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    contact_name: '',
    phone: '',
    email: '',
    property_name: '',
    city: '',
    property_type: '',
    social_media: '',
    description: '',
    best_time: '',
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.phone || !form.email || !form.property_name || !form.city || !form.property_type) {
      toast.error('Completá todos los campos obligatorios');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/owner-contact', form);
      toast.success('Formulario enviado exitosamente');
      setSubmitted(true);
    } catch {
      toast.error('Error al enviar el formulario. Intentá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F4F0] dark:bg-[#121212]">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              {brandData?.logo_url ? (
                <img src={resolveImageUrl(brandData.logo_url)} alt="" className="w-10 h-10 rounded-lg object-contain" />
              ) : (
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#264639]">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="font-bold text-xl text-[#264639] dark:text-[#6CC4A1]" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
                {brandData?.site_name || 'Estadía 365'}
              </span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/properties">
                <Button variant="outline" className="hidden sm:inline-flex border-[#264639] dark:border-[#6CC4A1] text-[#264639] dark:text-[#6CC4A1]">
                  Ver alojamientos
                </Button>
              </Link>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-20 pb-16">
        {/* Hero */}
        <section className="bg-[#264639] py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
              Sumá tu alojamiento a Estadía 365
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Ayudamos a hoteles, cabañas, departamentos y casas a captar más reservas a través de Airbnb, redes sociales y gestión profesional.
            </p>
          </div>
        </section>

        {/* Form Section */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 -mt-10">
          <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-xl p-6 sm:p-10" data-testid="owner-form-container">
            {submitted ? (
              <div className="text-center py-12" data-testid="form-success">
                <CheckCircle2 className="w-16 h-16 text-[#264639] dark:text-[#6CC4A1] mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-[#1C1917] dark:text-[#F5F4F0] mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
                  ¡Formulario enviado!
                </h2>
                <p className="text-[#57534E] dark:text-[#A8A29E] mb-6">
                  Recibimos tu información. Nos pondremos en contacto a la brevedad.
                </p>
                <Button onClick={() => { setSubmitted(false); setForm({ contact_name: '', phone: '', email: '', property_name: '', city: '', property_type: '', social_media: '', description: '', best_time: '' }); }} variant="outline" className="border-[#264639] dark:border-[#6CC4A1] text-[#264639] dark:text-[#6CC4A1]">
                  Enviar otro formulario
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <h2 className="text-xl font-bold text-[#1C1917] dark:text-[#F5F4F0] mb-6" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
                    Datos de contacto
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#57534E] dark:text-[#A8A29E] mb-1.5 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Nombre *</label>
                      <Input value={form.contact_name} onChange={e => handleChange('contact_name', e.target.value)} placeholder="Tu nombre completo" className="dark:bg-[#2A2A2A] dark:border-[#444] dark:text-[#F5F4F0]" data-testid="owner-contact-name" required />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#57534E] dark:text-[#A8A29E] mb-1.5 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Teléfono / WhatsApp *</label>
                      <Input value={form.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="+595 ..." className="dark:bg-[#2A2A2A] dark:border-[#444] dark:text-[#F5F4F0]" data-testid="owner-phone" required />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-sm font-medium text-[#57534E] dark:text-[#A8A29E] mb-1.5 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email *</label>
                      <Input type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} placeholder="tu@email.com" className="dark:bg-[#2A2A2A] dark:border-[#444] dark:text-[#F5F4F0]" data-testid="owner-email" required />
                    </div>
                  </div>
                </div>

                <div className="border-t border-[#E7E5E4] dark:border-[#333] pt-8">
                  <h2 className="text-xl font-bold text-[#1C1917] dark:text-[#F5F4F0] mb-6" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
                    Datos del alojamiento
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#57534E] dark:text-[#A8A29E] mb-1.5 flex items-center gap-1.5"><Home className="w-3.5 h-3.5" /> Nombre del alojamiento *</label>
                      <Input value={form.property_name} onChange={e => handleChange('property_name', e.target.value)} placeholder="Nombre de tu propiedad" className="dark:bg-[#2A2A2A] dark:border-[#444] dark:text-[#F5F4F0]" data-testid="owner-property-name" required />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#57534E] dark:text-[#A8A29E] mb-1.5 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Ciudad / Localidad *</label>
                      <Input value={form.city} onChange={e => handleChange('city', e.target.value)} placeholder="Ciudad donde se encuentra" className="dark:bg-[#2A2A2A] dark:border-[#444] dark:text-[#F5F4F0]" data-testid="owner-city" required />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#57534E] dark:text-[#A8A29E] mb-1.5 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Tipo de alojamiento *</label>
                      <select
                        value={form.property_type}
                        onChange={e => handleChange('property_type', e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background dark:bg-[#2A2A2A] dark:border-[#444] dark:text-[#F5F4F0]"
                        data-testid="owner-property-type"
                        required
                      >
                        <option value="">Seleccionar...</option>
                        {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#57534E] dark:text-[#A8A29E] mb-1.5 flex items-center gap-1.5"><Instagram className="w-3.5 h-3.5" /> Instagram / Facebook / Web</label>
                      <Input value={form.social_media} onChange={e => handleChange('social_media', e.target.value)} placeholder="@tuinstagram o sitio web" className="dark:bg-[#2A2A2A] dark:border-[#444] dark:text-[#F5F4F0]" data-testid="owner-social" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-sm font-medium text-[#57534E] dark:text-[#A8A29E] mb-1.5 block">Contanos brevemente sobre tu alojamiento</label>
                      <Textarea value={form.description} onChange={e => handleChange('description', e.target.value)} placeholder="Capacidad, amenities, características especiales..." rows={4} className="dark:bg-[#2A2A2A] dark:border-[#444] dark:text-[#F5F4F0]" data-testid="owner-description" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-sm font-medium text-[#57534E] dark:text-[#A8A29E] mb-1.5 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Mejor día y horario para contactarte</label>
                      <Input value={form.best_time} onChange={e => handleChange('best_time', e.target.value)} placeholder="Ej: Lunes a viernes de 9 a 18hs" className="dark:bg-[#2A2A2A] dark:border-[#444] dark:text-[#F5F4F0]" data-testid="owner-best-time" />
                    </div>
                  </div>
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-[#264639] hover:bg-[#1C352B] text-white py-6 text-lg" data-testid="owner-submit-btn">
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando...</>
                  ) : (
                    <><Send className="w-5 h-5 mr-2" /> Enviar formulario</>
                  )}
                </Button>
              </form>
            )}
          </div>
        </section>

        {/* FAQ Section for Owners */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-16" data-testid="owner-faq-section">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <HelpCircle className="w-5 h-5 text-[#C36F4D]" />
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#C36F4D]">Para propietarios</p>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1C1917] dark:text-[#F5F4F0]" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
              Preguntas frecuentes
            </h2>
          </div>

          <div className="space-y-3">
            {OWNER_FAQS.map((faq, idx) => (
              <div key={`owner-faq-${faq.q.substring(0, 20)}`} className="bg-white dark:bg-[#1E1E1E] rounded-xl border border-[#E7E5E4] dark:border-[#333] overflow-hidden" data-testid={`owner-faq-${idx}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                >
                  <span className="font-medium text-[#1C1917] dark:text-[#F5F4F0] text-sm sm:text-base pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#78716C] dark:text-[#8C857E] flex-shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-4">
                    <p className="text-[#57534E] dark:text-[#A8A29E] text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-16 mb-8">
          <div className="bg-[#264639] rounded-2xl p-8 sm:p-12 text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
              ¿Tenés un alojamiento, hotel o inmueble con potencial turístico?
            </h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              En Estadía 365 podemos ayudarte a convertirlo en una opción visible, atractiva y reservable para huéspedes de todo el mundo.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <Button className="bg-[#C36F4D] text-white hover:bg-[#A85A3B] px-8 font-bold" data-testid="cta-form-btn">
                  <Send className="w-4 h-4 mr-2" /> Completar formulario
                </Button>
              </a>
              <a href="https://wa.me/595982188147?text=Hola%2C%20quiero%20sumar%20mi%20alojamiento" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white/80 text-white hover:bg-white/10 px-8 font-bold" data-testid="cta-whatsapp-btn">
                  <Phone className="w-4 h-4 mr-2" /> Hablar por WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
